import { useState, useEffect } from 'react';
import {
  Typography, Table, Tag, Button, Space, DatePicker, Select,
  message, Spin, Empty
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  getCurrentOrgId,
  formatDate,
  formatVehiclePublicLabel,
  getClientTripDisplayColor,
} from '@ubus/shared';
import type { Trip } from '@ubus/shared';
import type { ColumnsType } from 'antd/es/table';
import { translateClientTripStatus, translateTripStatusFilter } from '../utils/clientStatusLabels';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function TripHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    getCurrentOrgId().then(setOrgId);
  }, []);

  useEffect(() => {
    if (orgId) void fetchTrips();
  }, [orgId, dateRange, statusFilter]);

  const fetchTrips = async () => {
    if (!orgId) return;
    setLoading(true);

    try {
      let query = supabase
        .from('trips')
        .select(`
          *,
          vehicles (*),
          drivers (*)
        `)
        .eq('org_id', orgId);

      if (dateRange && dateRange[0] && dateRange[1]) {
        query = query
          .gte('date', dateRange[0].format('YYYY-MM-DD'))
          .lte('date', dateRange[1].format('YYYY-MM-DD'));
      } else {
        const threeMonthsAgo = dayjs().subtract(90, 'day').format('YYYY-MM-DD');
        query = query.gte('date', threeMonthsAgo);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      query = query
        .order('date', { ascending: false })
        .order('pickup_time', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;
      setTrips(data || []);
    } catch (err: unknown) {
      message.error(t('trips.loadFailed', {
        message: err instanceof Error ? err.message : 'Unknown error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    'all',
    'scheduled',
    'dispatched',
    'in_progress',
    'completed',
    'cancelled',
  ].map((value) => ({
    label: translateTripStatusFilter(t, value),
    value,
  }));

  const columns: ColumnsType<Trip> = [
    {
      title: t('common.date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => formatDate(date),
      sorter: (a, b) => a.date.localeCompare(b.date),
    },
    {
      title: t('common.pickup'),
      dataIndex: 'pickup_address',
      key: 'pickup_address',
      ellipsis: true,
    },
    {
      title: t('common.dropoff'),
      dataIndex: 'dropoff_address',
      key: 'dropoff_address',
      ellipsis: true,
    },
    {
      title: t('common.passengers'),
      dataIndex: 'passenger_count',
      key: 'passenger_count',
      width: 100,
      align: 'center',
    },
    {
      title: t('common.vehicle'),
      key: 'vehicle',
      width: 110,
      render: (_: unknown, record) =>
        record.vehicles ? (
          <Tag>{formatVehiclePublicLabel(record.vehicles)}</Tag>
        ) : (
          <Text type="secondary">{t('common.noData')}</Text>
        ),
    },
    {
      title: t('common.driver'),
      dataIndex: ['drivers', 'name'],
      key: 'driver',
      width: 130,
      render: (name: string) => name || <Text type="secondary">{t('common.noData')}</Text>,
    },
    {
      title: t('common.statusLabel'),
      key: 'status',
      width: 130,
      render: (_: string, record) => (
        <Tag color={getClientTripDisplayColor(record)}>
          {translateClientTripStatus(t, record)}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/trip/${record.id}`)}
        >
          {t('common.open')}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="client-page-header">
        <div>
          <Title level={3} className="client-page-title">{t('trips.title')}</Title>
          <Text className="client-page-subtitle">
            {t('trips.subtitle', { count: trips.length })}
          </Text>
        </div>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            disabledDate={(d) => !!d && d > dayjs()}
            allowClear
            placeholder={[t('trips.dateRangeStart'), t('trips.dateRangeEnd')]}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            options={statusOptions}
          />
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : trips.length === 0 ? (
        <Empty description={t('trips.noTripsInRange')} />
      ) : (
        <Table
          columns={columns}
          dataSource={trips}
          rowKey="id"
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total) => t('trips.showTotal', { total }),
          }}
          scroll={{ x: 900 }}
        />
      )}
    </div>
  );
}

export default TripHistory;
